/**
 * Discovery V2 API Client
 */

import { apiClient } from '../client';
import type { ApiError } from '@/lib/types/errors';
import { err, ok, type Result } from '@/lib/types/result';

const PUBLIC_CATALOGUE_REQUEST_OPTIONS = { credentials: 'omit' } as const;

export interface LevelFilter {
  id: number;
  name: string;
}

export interface StudioFilter {
  id: string;
  name: string;
}

export interface DiscoveryFiltersData {
  levels: LevelFilter[];
  genres: string[];
  studios: StudioFilter[];
}

interface InstructorSummary {
  id: string;
  name: string;
}

interface StudioSummary {
  id: string;
  name: string;
}

export interface ClassTimeGrouped {
  id: string;
  name: string;
  level?: string;
  startTime: string; // RFC3339 format: "2026-03-17T14:30:00+08:00" - matches backend JSON field
  endTime: string | null; // RFC3339 or null (backend end_datetime NULL)
  instructor: InstructorSummary;
  studio: StudioSummary;
}

// Generic pagination interface
export interface Pagination {
  cursor: string;
  hasMore: boolean;
}

// Filter pill for display
export interface FilterPill {
  type: 'level' | 'genre' | 'studio';
  id: string;
  name: string;
}

// Instructor V2 type
export interface InstructorV2 {
  id: string;
  name: string;
  image?: string | null;
  studios?: { id: string; name: string; color: string }[];
  genres: string[];
  averageRating?: number;
  reviewCount?: number;
}

// Studio location type
interface StudioLocation {
  id: string;
  name: string;
  address?: string;
}

// Studio V2 type
export interface StudioV2 {
  id: string;
  name: string;
  logoUrl?: string | null;
  locations: StudioLocation[];
  averageRating?: number;
  reviewCount?: number;
}

// Studios response
export interface StudiosResponse {
  studios: StudioV2[];
  pagination: Pagination;
}

interface ClassesPagination {
  cursor: string;
  hasMore: boolean;
  asOf?: string;
}

export interface ClassesTimeGroupedResponse {
  data: Record<string, Record<string, ClassTimeGrouped[]>>;
  pagination: ClassesPagination;
}

export const getDiscoveryFilters = async (): Promise<Result<DiscoveryFiltersData, ApiError>> => {
  try {
    const response = await apiClient.get<DiscoveryFiltersData>(
      '/v2/discovery/filters',
      PUBLIC_CATALOGUE_REQUEST_OPTIONS
    );

    if (!response.ok) {
      return err(response.error);
    }

    return ok(response.value);
  } catch (error) {
    return err({
      type: 'network',
      message: error instanceof Error ? error.message : 'Failed to fetch discovery filters',
    });
  }
};

/**
 * Query function for React Query - unwraps the Result type
 */
export const getDiscoveryFiltersQueryFn = async (): Promise<DiscoveryFiltersData> => {
  const result = await getDiscoveryFilters();

  if (!result.ok) {
    throw new Error(result.error.message);
  }

  return result.value;
};

export const getClasses = async (
  startDate: string,
  endDate: string,
  filters?: {
    levels?: string[];
    genres?: string[];
    studios?: string[];
    cursor?: string;
    search?: string;
    favourites?: boolean;
  }
): Promise<ClassesTimeGroupedResponse> => {
  const params = new URLSearchParams({
    startDate,
    endDate,
  });

  if (filters?.levels?.length) {
    filters.levels.forEach((level) => params.append('levels', level));
  }

  if (filters?.genres?.length) {
    filters.genres.forEach((genre) => params.append('genres', genre));
  }

  if (filters?.studios?.length) {
    filters.studios.forEach((studio) => params.append('studios', studio));
  }

  if (filters?.cursor) {
    params.append('cursor', filters.cursor);
  }

  if (filters?.search) {
    params.append('search', filters.search);
  }

  if (filters?.favourites) {
    params.append('favourites', 'true');
  }

  const result = await apiClient.get<ClassesTimeGroupedResponse>(
    `/v2/classes?${params.toString()}`,
    filters?.favourites ? undefined : PUBLIC_CATALOGUE_REQUEST_OPTIONS
  );

  if (!result.ok) {
    throw new Error('Failed to fetch classes');
  }

  return result.value;
};

export const getInstructors = async (options?: {
  search?: string;
  studios?: string[];
  genres?: string[];
  cursor?: string;
  limit?: number;
  favourites?: boolean;
}): Promise<{ instructors: InstructorV2[]; pagination: Pagination }> => {
  const params = new URLSearchParams();

  if (options?.search) {
    params.append('search', options.search);
  }
  if (options?.studios?.length) {
    options.studios.forEach((s) => params.append('studios', s));
  }
  if (options?.genres?.length) {
    options.genres.forEach((g) => params.append('genres', g));
  }
  if (options?.cursor) {
    params.append('cursor', options.cursor);
  }
  if (options?.limit !== undefined) {
    params.append('limit', String(options.limit));
  }
  if (options?.favourites) {
    params.append('favourites', 'true');
  }

  const queryString = params.toString();
  const endpoint = `/v2/instructors${queryString ? `?${queryString}` : ''}`;

  const result = await apiClient.get<{
    instructors: InstructorV2[];
    pagination: Pagination;
  }>(endpoint, options?.favourites ? undefined : PUBLIC_CATALOGUE_REQUEST_OPTIONS);

  if (!result.ok) {
    throw new Error('Failed to fetch instructors');
  }

  // Return the data directly (API client already extracted it from the wrapper)
  return result.value;
};

export const getStudios = async (options?: {
  cursor?: string;
  limit?: number;
  search?: string;
  genres?: string[];
  favourites?: boolean;
}): Promise<StudiosResponse> => {
  const params = new URLSearchParams();

  if (options?.cursor) {
    params.append('cursor', options.cursor);
  }
  if (options?.limit !== undefined) {
    params.append('limit', String(options.limit));
  }
  if (options?.search) {
    params.append('search', options.search);
  }
  if (options?.genres && options.genres.length > 0) {
    options.genres.forEach((g) => params.append('genres', g));
  }
  if (options?.favourites) {
    params.append('favourites', 'true');
  }

  const queryString = params.toString();
  const endpoint = `/v2/studios${queryString ? `?${queryString}` : ''}`;

  const response = await apiClient.get<StudiosResponse>(
    endpoint,
    options?.favourites ? undefined : PUBLIC_CATALOGUE_REQUEST_OPTIONS
  );
  if (!response.ok) {
    throw new Error(response.error?.message || 'Failed to fetch studios');
  }

  const data = response.value;

  // Log studio IDs for debugging
  if (data.studios && data.studios.length > 0) {
    const { logger } = await import('@/lib/utils/logger');
    logger.info('getStudios', 'Fetched studios list', {
      count: data.studios.length,
      studioIds: data.studios.map((s) => ({ id: s.id, name: s.name })),
    });
  }

  return data;
};

// Nearby studio-location type (flat, from /v2/studios/nearby)
export interface NearbyStudioLocation {
  id: string;
  studioId: string;
  studioName: string;
  studioSlug: string;
  studioLogo?: string | null;
  studioColor: string;
  name: string;
  address?: string;
  latitude: number | null;
  longitude: number | null;
  distanceKm: number;
  averageRating?: number;
  reviewCount?: number;
}

export interface NearbyStudiosResponse {
  locations: NearbyStudioLocation[];
  center: { lat: number; lng: number };
  radiusKm: number;
}

export const getNearbyStudios = async (params: {
  lat: number;
  lng: number;
  radius?: number;
  limit?: number;
}): Promise<NearbyStudiosResponse> => {
  const searchParams = new URLSearchParams({
    lat: String(params.lat),
    lng: String(params.lng),
  });

  if (params.radius) {
    searchParams.append('radius', String(params.radius));
  }
  if (params.limit) {
    searchParams.append('limit', String(params.limit));
  }

  const endpoint = `/v2/studios/nearby?${searchParams.toString()}`;
  const result = await apiClient.get<NearbyStudiosResponse>(endpoint);

  if (!result.ok) {
    throw new Error('Failed to fetch nearby studios');
  }

  return result.value;
};
